/**
 * DeleteAccountModal - Modal de confirmación para cancelar/eliminar cuenta
 * Muestra advertencias sobre la pérdida de datos y solicita confirmación
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, Trash2 } from 'lucide-react';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  userName: string;
  queuePoints: number;
  isVIP: boolean;
  totalCompletedServices: number;
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  userName,
  queuePoints,
  isVIP,
  totalCompletedServices,
}: DeleteAccountModalProps) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const CONFIRM_WORD = 'ELIMINAR';
  const isConfirmValid = confirmText.toUpperCase() === CONFIRM_WORD;

  const handleConfirm = async () => {
    if (!isConfirmValid) return;

    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
      setConfirmText('');
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setConfirmText('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-account-modal-title"
        className="bg-surface rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-danger px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <AlertTriangle className="text-white" size={24} />
            </div>
            <h2 id="delete-account-modal-title" className="text-xl font-bold text-white">
              {t('deleteAccount.title', 'Cancelar Cuenta')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            aria-label={t('common.close', 'Cerrar')}
            className="p-2 hover:bg-white/20 rounded-full transition disabled:opacity-50"
          >
            <X className="text-white" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-danger/10 border border-danger/30 rounded-xl p-4">
            <p className="text-danger font-medium text-center">
              {t('deleteAccount.warning', 'Esta accion no se puede deshacer')}
            </p>
          </div>

          {/* User Info */}
          <div className="text-center py-2">
            <p className="text-ink-muted">
              {t('deleteAccount.confirmMessage', 'Estas a punto de cancelar la cuenta de:')}
            </p>
            <p className="text-lg font-bold text-ink mt-1">
              {userName}
            </p>
          </div>

          {/* What will be lost */}
          <div className="bg-surface-2 rounded-xl p-4 space-y-3">
            <p className="font-semibold text-ink text-sm">
              {t('deleteAccount.whatYouLose', 'Perderas:')}
            </p>

            <div className="space-y-2">
              {/* Points */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-2xl">⭐</span>
                <div className="flex-1">
                  <p className="text-ink-muted">
                    {t('deleteAccount.loyaltyPoints', 'Puntos de lealtad')}
                  </p>
                  <p className="text-danger font-bold">
                    {queuePoints} {t('deleteAccount.points', 'puntos')}
                  </p>
                </div>
              </div>

              {/* VIP Status */}
              {isVIP && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-2xl">👑</span>
                  <div className="flex-1">
                    <p className="text-ink-muted">
                      {t('deleteAccount.vipStatus', 'Status VIP')}
                    </p>
                    <p className="text-danger font-bold">
                      {t('deleteAccount.vipLost', 'Acceso a citas preferentes')}
                    </p>
                  </div>
                </div>
              )}

              {/* Stamps/Rewards */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-2xl">🎁</span>
                <div className="flex-1">
                  <p className="text-ink-muted">
                    {t('deleteAccount.stampsRewards', 'Sellos y recompensas')}
                  </p>
                  <p className="text-danger font-bold">
                    {t('deleteAccount.allStamps', 'Todos tus sellos acumulados')}
                  </p>
                </div>
              </div>

              {/* History */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-2xl">📋</span>
                <div className="flex-1">
                  <p className="text-ink-muted">
                    {t('deleteAccount.serviceHistory', 'Historial de servicios')}
                  </p>
                  <p className="text-danger font-bold">
                    {totalCompletedServices} {t('deleteAccount.completedServices', 'servicios completados')}
                  </p>
                </div>
              </div>

              {/* Account Data */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-2xl">🔐</span>
                <div className="flex-1">
                  <p className="text-ink-muted">
                    {t('deleteAccount.accountData', 'Datos de tu cuenta')}
                  </p>
                  <p className="text-danger font-bold">
                    {t('deleteAccount.permanentDelete', 'Eliminacion permanente')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-ink-muted">
              {t('deleteAccount.typeToConfirm', 'Escribe')} <span className="text-danger font-bold">{CONFIRM_WORD}</span> {t('deleteAccount.toConfirm', 'para confirmar:')}
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_WORD}
              disabled={isDeleting}
              className="w-full px-4 py-3 border-2 border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-danger focus:border-danger/30 text-center font-mono text-lg disabled:opacity-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1 py-3 px-4 bg-surface-2 text-ink-muted font-semibold rounded-xl hover:bg-surface-3 transition disabled:opacity-50"
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="flex-1 py-3 px-4 bg-danger text-white font-semibold rounded-xl hover:bg-danger/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                {t('deleteAccount.deleting', 'Eliminando...')}
              </>
            ) : (
              <>
                <Trash2 size={18} />
                {t('deleteAccount.confirmDelete', 'Eliminar Cuenta')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
